import { Card, Col, Row } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import ReactSelect from 'components/base/ReactSelect';
import {
  getAllCategoryTypes,
  getAllCategories,
  getSubCategories,
  getAllBrands
} from 'api/categories/_requests';
import { getVendorProducts } from 'api/products/_requests';
import { AddCouponSchema } from 'pages/apps/e-commerce/admin/model';

type Option = { value: string; label: string };
type NamedEntity = { _id: string; name: string };

const APPLY_TO_OPTIONS: Option[] = [
  { value: 'all', label: 'All' },
  { value: 'categoryType', label: 'Category Type' },
  { value: 'category', label: 'Category' },
  { value: 'subcategory', label: 'Subcategory' },
  { value: 'brand', label: 'Brand' },
  { value: 'product', label: 'Products' }
];

export default function CouponTargetCard({
  className
}: {
  className?: string;
}) {
  const { control, watch, setValue, formState } =
    useFormContext<AddCouponSchema>();
  const appliesTo = watch('appliesTo');

  const { data: rawOptions, isFetching } = useQuery<NamedEntity[]>({
    queryKey: ['coupon-targets', appliesTo],
    enabled: !!appliesTo && appliesTo !== 'all',
    queryFn: () => {
      switch (appliesTo) {
        case 'categoryType':
          return getAllCategoryTypes();
        case 'category':
          return getAllCategories();
        case 'subcategory':
          return getSubCategories();
        case 'brand':
          return getAllBrands();
        case 'product':
          return getVendorProducts();
        default:
          return Promise.resolve([]);
      }
    }
  });
  const selectionOptions: Option[] = React.useMemo(
    () => (rawOptions ?? []).map(o => ({ value: o._id, label: o.name })),
    [rawOptions]
  );

  React.useEffect(() => {
    setValue('targetIds', []);
  }, [appliesTo, setValue]);

  const getLabel = (scope: Option | string | undefined) => {
    const value = typeof scope === 'string' ? scope : scope?.value;
    const baseLbl = APPLY_TO_OPTIONS.find(o => o.value === value)?.label ?? '';
    const pluralMap: Record<string, string> = {
      categoryType: 'Category Types',
      category: 'Categories',
      subcategory: 'Sub‑categories'
    };

    return pluralMap[value as string] || baseLbl;
  };

  return (
    <Card className={className}>
      <Card.Body>
        <h4 className="mb-4">Organize</h4>
        <Row className="gx-3 gy-4">
          <Col xs={12}>
            <h5 className="mb-2 text-body-highlight">Apply to</h5>
            <Controller
              name="appliesTo"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  isMulti={false}
                  options={APPLY_TO_OPTIONS}
                  value={APPLY_TO_OPTIONS.find(o => o.value === field.value)}
                  onChange={opt =>
                    field.onChange((opt as Option | null)?.value ?? 'all')
                  }
                  placeholder="Where will this coupon apply?"
                />
              )}
            />
            <div className="text-danger">
              {formState.errors.appliesTo?.message}
            </div>
          </Col>

          {appliesTo !== 'all' && (
            <Col xs={12}>
              <h5 className="mb-2 text-body-highlight">
                Select&nbsp;{getLabel(appliesTo)}
              </h5>

              <Controller
                name="targetIds"
                control={control}
                render={({ field }) => (
                  <ReactSelect
                    {...field}
                    isMulti
                    closeMenuOnSelect={false}
                    isLoading={isFetching}
                    isDisabled={isFetching || selectionOptions.length === 0}
                    options={selectionOptions}
                    placeholder={
                      isFetching ? 'Loading...' : `Choose ${appliesTo}`
                    }
                    value={(field.value ?? []).map(
                      id =>
                        selectionOptions.find(o => o.value === id) ?? {
                          value: id,
                          label: 'Unknown'
                        }
                    )}
                    onChange={opts =>
                      field.onChange((opts as Option[]).map(o => o.value))
                    }
                  />
                )}
              />
              <div className="text-danger">
                {formState.errors.targetIds?.message}
              </div>
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}
